import { CollapsibleFilters } from '../components/common/CollapsibleFilters';
import { PaymentReconciliation } from '../components/billing/BillingWorkflowTools'
import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Input,
  Select,
  Option,
  FormControl,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/joy'
import {
  Plus as Add,
  DownloadSimple as Download,
  Receipt,
  Funnel as FilterList,
} from '@phosphor-icons/react'
import { paymentsApi } from '../lib/billingApi'
import { ResponsiveTable } from '../components/common/ResponsiveTable'
import type { PaymentRecord, PaymentFilters } from '../types/billing'
import toast from 'react-hot-toast'
import SendReceiptModal from '../components/billing/SendReceiptModal'

export default function PaymentList() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Filter state
  const [filters, setFilters] = useState<PaymentFilters>({
    is_reconciled: searchParams.has('is_reconciled') ? searchParams.get('is_reconciled') === 'true' : undefined,
    invoice_id: searchParams.get('invoice') ? Number(searchParams.get('invoice')) : undefined,
    payment_method: searchParams.get('method') || undefined,
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
  })

  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 20

  // Send-receipt modal (which payment is being emailed)
  const [receiptPaymentId, setReceiptPaymentId] = useState<number | null>(null)

  const handleDownloadReceipt = async (payment: PaymentRecord) => {
    try {
      const blob = await paymentsApi.downloadReceipt(payment.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `receipt-${payment.id}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download receipt')
    }
  }

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters.payment_method, filters.date_from, filters.date_to, filters.invoice_id, filters.search, filters.is_reconciled])

  // Fetch payments
  const { data, isLoading, error } = useQuery({
    queryKey: ['payments', filters, currentPage],
    queryFn: () => paymentsApi.list({ ...filters, page: currentPage }),
  })

  const totals = useQuery({queryKey:['payments', 'totals', filters],queryFn:()=>paymentsApi.totals(filters)})

  const handleFilterChange = (newFilters: Partial<PaymentFilters>) => {
    const updatedFilters = { ...filters, ...newFilters }
    setFilters(updatedFilters)

    // Update URL params
    const params = new URLSearchParams()
    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') params.set(key, String(value))
    })
    setSearchParams(params)
  }

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'credit_card':
        return '💳'
      case 'bank_transfer':
        return '🏦'
      case 'check':
        return '📄'
      case 'cash':
        return '💵'
      default:
        return '💰'
    }
  }

  const getMethodLabel = (method: string) => {
    return method.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  if (error) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Error loading payments. Please try again.
      </Alert>
    )
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: { xs: 3, md: 4 }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box>
          <Typography level="h3">Payments</Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            Track and manage payment records
          </Typography>
        </Box>
        <Button
          component={Link}
          to="new"
          startDecorator={<Add />}
          variant="solid"
          color="primary"
        >
          Record Payment
        </Button>
      </Stack>

      {/* Summary Cards */}
      {data && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
              <Card variant="soft" color="primary">
                <CardContent>
                  <Typography level="body-sm">Total Payments</Typography>
                  <Typography level="h3">{data.count}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Card variant="soft" color="success">
                <CardContent>
                  <Typography level="body-sm">Net Collected · All Filtered Payments</Typography>
                  <Typography level="h3">
                    {totals.isError ? 'Unavailable' : totals.data ? formatCurrency(totals.data.net_received) : '…'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Filters */}
      <CollapsibleFilters><Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControl size="sm" sx={{ minWidth: 200 }}>
              <Input placeholder="Search reference or notes" value={filters.search || ''} onChange={e=>handleFilterChange({search:e.target.value})}/>
            </FormControl>
            <FormControl size="sm">
              <Select aria-label="Reconciliation" value={filters.is_reconciled === undefined ? 'all' : String(filters.is_reconciled)} onChange={(_,v)=>handleFilterChange({is_reconciled:v==='all'?undefined:v==='true'})}>
                <Option value="all">All payments</Option><Option value="false">Unreconciled</Option><Option value="true">Reconciled</Option>
              </Select>
            </FormControl>
            <FormControl size="sm">
              <Select
                placeholder="Payment Method"
                value={filters.payment_method}
                onChange={(_, value) => handleFilterChange({ payment_method: value as string })}
                startDecorator={<FilterList />}
              >
                <Option value="">All Methods</Option>
                <Option value="credit_card">Credit Card</Option>
                <Option value="bank_transfer">Bank Transfer</Option>
                <Option value="check">Check</Option>
                <Option value="cash">Cash</Option>
                <Option value="other">Other</Option>
              </Select>
            </FormControl>

            <FormControl size="sm">
              <Input
                type="date"
                placeholder="From date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange({ date_from: e.target.value })}
              />
            </FormControl>

            <FormControl size="sm">
              <Input
                type="date"
                placeholder="To date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange({ date_to: e.target.value })}
              />
            </FormControl>

            <Button
              variant="outlined"
              color="primary"
              size="sm"
              onClick={() => {
                setFilters({})
                setSearchParams(new URLSearchParams())
              }}
            >
              Clear Filters
            </Button>
          </Stack>
        </CardContent>
      </Card></CollapsibleFilters>

      {/* Payments Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data?.results && data.results.length > 0 ? (
        <ResponsiveTable<PaymentRecord>
          rows={data.results}
          getRowKey={(payment) => payment.id}
          columns={[
            {
              key: 'invoice',
              sortValue: (row) => row.invoice_number,
              header: 'Invoice',
              mobileVariant: 'header',
              render: (payment) => (
                <Link to={`/invoices/${payment.invoice_id}`}>
                  <Typography level="title-sm">
                    #{payment.invoice_number || payment.invoice_id}
                  </Typography>
                </Link>
              ),
            },
            {
              key: 'date',
              sortValue: (row) => row.payment_date ? new Date(row.payment_date) : null,
              header: 'Date',
              render: (payment) => (
                <Typography level="body-sm">{formatDate(payment.payment_date)}</Typography>
              ),
            },
            {
              key: 'customer',
              sortValue: (row) => row.customer_name,
              header: 'Customer',
              render: (payment) => (
                <Typography level="body-sm">{payment.customer_name || 'Unknown'}</Typography>
              ),
            },
            {
              key: 'amount',
              sortValue: (row) => Number(row.amount),
              header: 'Amount',
              render: (payment) => <PaymentReconciliation payment={payment}/>,
            },
            {
              key: 'method',
              sortValue: (row) => row.payment_method,
              header: 'Method',
              render: (payment) => (
                <Chip
                  size="sm"
                  variant="soft"
                  startDecorator={getMethodIcon(payment.payment_method)}
                >
                  {getMethodLabel(payment.payment_method)}
                </Chip>
              ),
            },
            {
              key: 'reference',
              sortValue: (row) => row.reference_number,
              header: 'Reference',
              render: (payment) => (
                <Typography level="body-sm">{payment.reference_number || '-'}</Typography>
              ),
            },
            {
              key: 'recorded_by',
              header: 'Recorded By',
              render: (payment) => (
                <Typography level="body-sm">{payment.created_by_name || 'System'}</Typography>
              ),
            },
          ]}
          rowActions={(payment) => (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Send Receipt">
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => setReceiptPaymentId(payment.id)}
                >
                  <Receipt />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => handleDownloadReceipt(payment)}
                >
                  <Download />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        />
      ) : (
        <Card variant="soft">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography level="body-lg" sx={{ mb: 2 }}>No payments found</Typography>
            <Button
              component={Link}
              to="/billing/payments/new"
              startDecorator={<Add />}
              variant="solid"
            >
              Record Your First Payment
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mt: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Typography level="body-sm" color="neutral">
          {(data?.count ?? 0) > 0
            ? `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, data!.count)}–${Math.min(currentPage * PAGE_SIZE, data!.count)} of ${data!.count} payments`
            : 'No payments'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage >= Math.ceil((data?.count ?? 0) / PAGE_SIZE)}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </Button>
        </Box>
      </Stack>

      <SendReceiptModal
        open={receiptPaymentId !== null}
        paymentId={receiptPaymentId}
        onClose={() => setReceiptPaymentId(null)}
      />
    </Box>
  )
}

// Grid component
const Grid: React.FC<{
  children: React.ReactNode
  container?: boolean
  spacing?: number
  xs?: number
  sm?: number
  md?: number
  lg?: number
}> = ({ children, container, spacing = 0, xs, sm, md, lg }) => {
  if (container) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          margin: spacing ? -spacing / 2 : 0,
        }}
      >
        {children}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        padding: spacing ? spacing / 2 : 0,
        width: {
          xs: xs ? `${(xs / 12) * 100}%` : '100%',
          sm: sm ? `${(sm / 12) * 100}%` : undefined,
          md: md ? `${(md / 12) * 100}%` : undefined,
          lg: lg ? `${(lg / 12) * 100}%` : undefined,
        },
      }}
    >
      {children}
    </Box>
  )
}
